import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { SearchHit } from "@/lib/types";
import { useContactSearch } from "@/components/CommandPalette";
import { useToast } from "@/components/Toaster";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { useContactNames } from "@/features/billing/useContactNames";
import { PARTNER_STATUS, PAYOUT_METHODS, usePrograms, type Affiliate } from "./affiliates";

const CODE_RE = /^[A-Za-z0-9_-]{2,40}$/;

/** New partner (affiliates_create) or change one (affiliates_update): status, program, recruiter, payout details, notes. */
export function PartnerDialog({ partner, partners, onClose }: { partner?: Affiliate; partners: Affiliate[]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const programs = usePrograms().data?.programs ?? [];
  const [q, setQ] = useState("");
  const [who, setWho] = useState<SearchHit | null>(null);
  const [code, setCode] = useState(partner?.code ?? "");
  const [status, setStatus] = useState(partner?.status ?? "active");
  const [program, setProgram] = useState<number | "">(partner?.default_program_id ?? (partner ? "" : (programs.find((p) => p.active)?.id ?? "")));
  const [parent, setParent] = useState<number | "">(partner?.parent_affiliate_id ?? "");
  const [method, setMethod] = useState(partner?.payout_method ?? "");
  const [email, setEmail] = useState(partner?.payout_email ?? "");
  const [notes, setNotes] = useState(partner?.notes ?? "");
  const search = useContactSearch(q);
  const name = useContactNames(partners.map((p) => p.contact_id));
  const hits = !partner && q.trim() ? (search.data?.hits ?? []).filter((h) => !h.archived).slice(0, 8) : [];
  const save = useMutation({
    mutationFn: () =>
      partner
        ? api<Affiliate>(`/api/v1/affiliates/${partner.id}`, {
            method: "PATCH",
            json: {
              status: status !== partner.status ? status : undefined,
              program_id: program !== (partner.default_program_id ?? "") && program !== "" ? program : undefined,
              parent_affiliate_id: parent !== (partner.parent_affiliate_id ?? "") ? (parent === "" ? 0 : parent) : undefined,
              payout_method: method !== (partner.payout_method ?? "") ? method : undefined,
              payout_email: email.trim() !== (partner.payout_email ?? "") ? email.trim() : undefined,
              notes: notes !== (partner.notes ?? "") ? notes : undefined,
            },
          })
        : api<Affiliate>("/api/v1/affiliates", {
            json: {
              contact_id: who!.contact_id,
              code: code.trim(),
              program_id: program || undefined,
              parent_affiliate_id: parent || undefined,
              payout_method: method || undefined,
              payout_email: email.trim() || undefined,
              notes: notes.trim() || undefined,
            },
          }),
    onSuccess: (a) => {
      void qc.invalidateQueries({ queryKey: ["affiliates"] });
      void qc.invalidateQueries({ queryKey: ["affiliate", a.id] });
      toast(partner ? `Saved ${a.code}` : `${who!.name || "They"} became partner ${a.code}`);
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  const codeOk = !!partner || CODE_RE.test(code.trim());
  const ready = partner ? true : !!who && codeOk;
  const others = partners.filter((p) => p.id !== partner?.id);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(600px,calc(100vw-2rem))]">
        <DialogTitle>{partner ? `Change partner ${partner.code}` : "New affiliate partner"}</DialogTitle>
        <DialogDescription>
          {partner
            ? "Commission rates come from the program. Changing the program or recruiter affects future sales only."
            : "Their links are account.<brand>.com/a/<code>. A recruiter earns the program's second-level commission on this partner's sales."}
        </DialogDescription>
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          {!partner &&
            (who ? (
              <div className="flex items-center justify-between rounded-md border px-3 py-2 sm:col-span-2">
                <span>
                  <span className="font-medium">{who.name}</span> <span className="text-xs text-[var(--color-muted-foreground)]">{who.email ?? ""} #{who.contact_id}</span>
                </span>
                <button className="text-xs text-[var(--color-primary)] hover:underline" onClick={() => setWho(null)}>
                  Change
                </button>
              </div>
            ) : (
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="pt-who">Person</Label>
                <Input id="pt-who" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email, phone or ID" />
                {hits.length > 0 && (
                  <ul className="max-h-48 overflow-y-auto rounded-md border">
                    {hits.map((h) => (
                      <li key={h.contact_id}>
                        <button className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-[var(--color-muted)]" onClick={() => setWho(h)}>
                          <span className="truncate">{h.name || "(no name)"}</span>
                          <span className="truncate text-xs text-[var(--color-muted-foreground)]">
                            {h.email ?? h.phone ?? ""} #{h.contact_id}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          {!partner ? (
            <div className="space-y-1">
              <Label htmlFor="pt-code">Code</Label>
              <Input id="pt-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="jsmith" aria-invalid={!!code && !codeOk} />
              {code && !codeOk && <p className="text-xs text-[var(--color-danger-fg)]">2 to 40 letters, numbers, dashes or underscores.</p>}
            </div>
          ) : (
            <div className="space-y-1">
              <Label htmlFor="pt-status">Status</Label>
              <Select id="pt-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                {Object.entries(PARTNER_STATUS).map(([k, [label]]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="pt-program">Program</Label>
            <Select id="pt-program" value={program} onChange={(e) => setProgram(e.target.value ? Number(e.target.value) : "")}>
              <option value="">{partner ? "No program" : "None"}</option>
              {programs.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.active ? "" : " (off)"}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="pt-parent">Recruited by</Label>
            <Select id="pt-parent" value={parent} onChange={(e) => setParent(e.target.value ? Number(e.target.value) : "")}>
              <option value="">Nobody</option>
              {others.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code}
                  {` (${name(p.contact_id)})`}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="pt-method">Usually paid by</Label>
            <Select id="pt-method" value={method} onChange={(e) => setMethod(e.target.value)}>
              <option value="">Not set</option>
              {PAYOUT_METHODS.map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="pt-email">PayPal or payout email</Label>
            <Input id="pt-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="pt-notes">Notes</Label>
            <Textarea id="pt-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!ready || save.isPending} onClick={() => save.mutate()}>
            {partner ? "Save" : "Make partner"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
